import { Box, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { messages } from '@databases/intl/messages';
import { useHoverState } from '@databases/react';
import React, { FunctionComponent, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import AddCommentIcon from './AddCommentIcon';
import CommentIcon from './CommentIcon';

const ViewThreadButtonStyles = xcss({
  ':hover': {
    cursor: 'pointer',
    backgroundColor: 'color.background.neutral.hovered',
  },
  borderRadius: '3px',
  paddingInline: 'space.025',
});

const AddThreadButtonStyles = xcss({
  borderRadius: '3px',
  ':hover': {
    cursor: 'pointer',
  },
});

export interface CellCommentsButtonProps {
  onClick: (e: React.MouseEvent) => void;
  commentToggled: boolean;
  hasThreads: boolean;
  commentCount: number;
  shouldShowHoverButton: boolean;
  valueId: string;
}

const CellCommentsButton: FunctionComponent<CellCommentsButtonProps> = ({
  onClick,
  commentToggled,
  hasThreads,
  commentCount,
  shouldShowHoverButton,
  valueId,
}) => {
  const { formatMessage } = useIntl();
  const [isHovering, hoverProps] = useHoverState();

  const commentString = useMemo(() => {
    return commentCount > 9 ? '9+' : commentCount.toString();
  }, [commentCount]);

  if (hasThreads) {
    return (
      <Tooltip
        position="top"
        content={formatMessage(messages['comments.show-comment-thread-button-tooltip'], {
          isMulti: commentCount,
        })}
      >
        <Box
          onClick={onClick}
          xcss={ViewThreadButtonStyles}
          id={`cell-view-thread-button-${valueId}`}
        >
          <Inline
            space="space.075"
            alignBlock="center"
            xcss={xcss({
              color: commentToggled ? 'color.text.accent.blue' : 'color.text.subtle',
            })}
          >
            <CommentIcon
              label={formatMessage(messages['comments.view-comment-thread-button-label'])}
              color={
                commentToggled
                  ? token('color.text.accent.blue')
                  : token('color.text.subtle')
              }
            />
            {commentString}
          </Inline>
        </Box>
      </Tooltip>
    );
  }

  if (shouldShowHoverButton) {
    return (
      <Tooltip
        position="top"
        content={formatMessage(messages['comments.add-comment-thread-button-label'])}
      >
        <Box
          xcss={AddThreadButtonStyles}
          onClick={onClick}
          {...hoverProps}
          id={`cell-add-thread-button-${valueId}`}
        >
          <AddCommentIcon
            label={formatMessage(messages['comments.add-comment-thread-button-label'])}
            color={isHovering ? token('color.text.subtle') : token('color.text.subtlest')}
          />
        </Box>
      </Tooltip>
    );
  }

  return null;
};

export default CellCommentsButton;
